<template>
  <v-container class="grey-lighten-3 full-height d-flex flex-column">
    <!-- Loading State -->
    <v-row v-if="loading" justify="center">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-2">Loading PI details...</p>
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-row v-else-if="!piData || Object.keys(piData).length === 0" justify="center">
      <v-col cols="12" class="text-center">
        <p class="text-grey">No PI data found</p>

      
      </v-col>
    </v-row>

    <!-- Printable Area -->
    <v-row v-else justify="center">
      <v-col cols="12" xl="10" lg="11" md="12">
        <div class="print-container" id="printable-area">
          <!-- Watermark -->
          <div class="watermark">PROFORMA INVOICE</div>
          
          <!-- Header Section -->
          <div class="header">
            <img
              v-if="getCurrentCompany?.logo_thumb"
              :src="`${getFileServerUrl.replace(/\/$/, '')}/${getCurrentCompany.logo_thumb}`"
              alt="Company Logo"
              class="company-logo"
            />
            <div v-else class="logo-placeholder">
              <v-icon icon="mdi-domain" size="60" color="grey-darken-2"></v-icon>
            </div>
            <div class="company-info">
              <strong>{{ getCurrentCompany?.name || 'Company Name' }}</strong><br>
              {{ getCurrentCompany?.address || 'Company Address' }}<br>
              Email: {{ getCurrentCompany?.email || 'info@company.com' }} | 
              Phone: {{ getCurrentCompany?.mobile || '+880123456789' }}
            </div>
          </div>

          <!-- PI Title -->
          <h2 class="pi-title">PROFORMA INVOICE (PI)</h2>

          <!-- PI No and Date -->
          <p class="pi-info">
            <strong>PI No:</strong> {{ piData.pi_no || 'N/A' }} &nbsp;&nbsp;&nbsp;
            <strong>PI Code:</strong> {{ piData.code || 'N/A' }} &nbsp;&nbsp;&nbsp;
            <strong>Date:</strong> {{ formatDate(piData.pi_date) }}
          </p>

          <!-- Supplier Information Section -->
          <div class="section-title">Supplier Information</div>
          <div class="flex-row">
            <div class="flex-col">
              <strong>Supplier:</strong> {{ piData.supplier?.supplier_name || 'N/A' }}<br>
              <strong>Supplier Code:</strong> {{ piData.supplier?.code || 'N/A' }}<br>
             
            </div>
            <div class="flex-col">
              <strong>Validity:</strong> {{ piData.validity ? formatDate(piData.validity) : 'N/A' }}<br>
              <strong>Status:</strong> {{ piData.status == 1 ? 'Posted' : 'Draft' }}<br>
              <strong>Approval Status:</strong> {{ piData.approval_status || 'Approved' }}
            </div>
          </div>

          <!-- Payment & Shipping Information Section -->
          <div class="section-title">Payment & Shipping Information</div>
          <div class="flex-row">
            <div class="flex-col">
              <strong>Currency:</strong> {{ piData.currency?.code || 'N/A' }} ({{ piData.currency?.name || '' }})<br>
              <strong>Bank:</strong> {{ piData.bank?.name || 'N/A' }}<br>
              <strong>Payment Term:</strong> {{ piData.paymentTerm?.name || piData.payment_term?.name || 'N/A' }}<br>
              <strong>Incoterm:</strong> {{ piData.incoterm?.name || 'N/A' }} ({{ piData.incoterm?.code || '' }})
            </div>
            <div class="flex-col">
              <strong>Loading Port:</strong> {{ piData.loadingPort?.name || piData.loading_port?.name || 'N/A' }}<span v-if="piData.loadingPort?.code || piData.loading_port?.code"> ({{ piData.loadingPort?.code || piData.loading_port?.code }})</span><br>
              <strong>Discharge Port:</strong> {{ piData.dischargePort?.name || piData.discharge_port?.name || 'N/A' }}<span v-if="piData.dischargePort?.code || piData.discharge_port?.code"> ({{ piData.dischargePort?.code || piData.discharge_port?.code }})</span><br>
              <strong>Shipping Method:</strong> {{ piData.shippingMethod?.name || piData.shipping_method?.name || 'N/A' }}<span v-if="piData.shippingMethod?.code || piData.shipping_method?.code"> ({{ piData.shippingMethod?.code || piData.shipping_method?.code }})</span>
            </div>
          </div>

         

          <!-- PI Items Section -->
          <div class="section-title">PI Items</div>
          <table class="items-table">
            <thead>
              <tr>
                <th style="width: 5%">#</th>
                <th style="width: 10%">Product Code</th>
                <th style="width: 25%">Product Name</th>
                <th style="width: 10%">UOM</th>
                <th style="width: 12%">Quantity</th>
                <th style="width: 14%">Rate ({{ piData.currency?.code || 'USD' }})</th>
                <th style="width: 14%">Amount ({{ piData.currency?.code || 'USD' }})</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in cart" :key="item.product_id || index">
                <td>{{ index + 1 }}</td>
                <td>{{ item.product_code || 'N/A' }}</td>
                <td>{{ item.product_name || 'N/A' }}</td>
                <td>{{ item.uom || 'PCS' }}</td>
                <td>{{ formatNumber(item.qty) }}</td>
                <td>{{ formatNumber(item.rate) }}</td>
                <td>{{ formatNumber(item.total) }}</td>
              </tr>
              <tr v-if="cart.length === 0">
                <td colspan="7" class="text-center">No items found</td>
              </tr>
            </tbody>
          </table>

          <!-- Summary Section -->
          <div class="section-title">Summary</div>
          <table class="summary-table">
            <tbody>
              <tr>
                <td><strong>Sub Total</strong></td>
                <td style="text-align:right;">{{ piData.currency?.code || '' }} {{ formatNumber(piData.sub_total || piData.total_amount || 0) }}</td>
              </tr>
              <tr>
                <td><strong>Discount</strong></td>
                <td style="text-align:right;">{{ piData.currency?.code || '' }} {{ formatNumber(piData.discount || 0) }}</td>
              </tr>
              <tr>
                <td><strong>Net Total</strong></td>
                <td style="text-align:right;">{{ piData.currency?.code || '' }} {{ formatNumber(piData.net_total || piData.total_amount || 0) }}</td>
              </tr>
              <tr>
                <td><strong>Total Amount</strong></td>
                <td style="text-align:right;"><strong>{{ piData.currency?.code || '' }} {{ formatNumber(piData.total_amount || 0) }}</strong></td>
              </tr>
            </tbody>
          </table>

          <!-- Remarks Section -->
          <div v-if="piData.remarks" class="remarks-section">
            <p><strong>Remarks:</strong> {{ piData.remarks }}</p>
          </div>

          <!-- Signature Section -->
          <div class="signature-area">
            <div class="signature-box">Prepared By</div>
            <div class="signature-box">Checked By</div>
            <div class="signature-box">Approved By</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Action Toolbar at Bottom (Hidden on Print) -->
    <v-row class="d-print-none mb-4" justify="center" v-if="!loading && piData">
      <v-col cols="12">
        <div class="text-center">
          
          <v-btn color="primary" class="mr-2" @click="printPage">
            <v-icon left>mdi-printer</v-icon> Print
          </v-btn>
          <v-btn color="success" @click="downloadPDF">
            <v-icon left>mdi-file-pdf-box</v-icon> Download PDF
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import formatDateView from "@/plugins/dateFormatView";
import html2pdf from "html2pdf.js";
import { ref, onMounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import { useStore } from "vuex";

export default {
  name: "ViewLcImportPi",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const loading = ref(false);
    const piData = ref({});
    const cart = ref([]);

    const formatDate = (date) => {
      if (!date) return "";
      return formatDateView(date);
    };

    const formatNumber = (num) => {
      if (!num && num !== 0) return "0.00";
      return Number(num).toFixed(2);
    };

    const loadPiData = async (id) => {
      if (!id) {
        toast.error("No PI ID provided");
        router.push({ path: "/lc/import-pi-record" });
        return;
      }

      loading.value = true;
      try {
        const data = await store.dispatch("getLcImportPiById", id);
        piData.value = data;
        cart.value = (data.details || []).map((item) => ({
          product_id: item.product_id,
          product_name: item.product?.name || "",
          product_code: item.product?.code || "",
          uom: item.product?.unit?.name || "PCS",
          qty: item.qty,
          rate: item.rate,
          total: item.amount || item.qty * item.rate,
        }));
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load PI data");
        router.push({ path: "/lc/import-pi-record" });
      } finally {
        loading.value = false;
      }
    };

    const goBack = () => {
      router.push({ path: "/lc/import-pi-record" });
    };

    const downloadPDF = async () => {
      const element = document.getElementById("printable-area");
      const opt = {
        margin: [0.5, 0.5],
        filename: `proforma-invoice-${piData.value.pi_no || piData.value.code}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "cm", format: "a4", orientation: "portrait" },
      };
      await html2pdf().set(opt).from(element).save();
    };

    const printPage = () => {
      const printContent = document.getElementById("printable-area").innerHTML;
      const printWindow = window.open("", "_blank", `width=${screen.width}, height=${screen.height}, left=0, top=0`);

      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Proforma Invoice - ${piData.value.pi_no || 'PI'}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              background: #fff;
              margin: 0;
              padding: 0;
            }
            .print-container {
              width: 800px;
              margin: auto;
              padding: 25px;
              position: relative;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
              margin-bottom: 15px;
            }
            .header img {
              width: 120px;
              margin-bottom: 10px;
            }
            .logo-placeholder {
              margin-bottom: 10px;
            }
            .company-info {
              font-size: 14px;
              margin-top: 5px;
            }
            h2, h3 {
              margin: 0 0 10px;
            }
            .pi-title {
              text-align: center;
              margin-bottom: 20px;
              font-size: 24px;
              font-weight: bold;
            }
            .pi-info {
              margin-bottom: 15px;
              font-size: 14px;
            }
            .section-title {
              background: #f2f2f2;
              padding: 8px;
              margin-top: 20px;
              font-weight: bold;
              border-left: 4px solid #555;
            }
            .flex-row {
              display: flex;
              justify-content: space-between;
              margin-top: 15px;
            }
            .flex-col {
              width: 48%;
              line-height: 22px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
              font-size: 14px;
            }
            table th, table td {
              border: 1px solid #999;
              padding: 8px;
              text-align: left;
            }
            table th {
              background: #eee;
              font-weight: bold;
            }
            .items-table td {
              text-align: left;
            }
            .items-table td:nth-child(3),
            .items-table td:nth-child(4),
            .items-table td:nth-child(5) {
              text-align: right;
            }
            .summary-table {
              width: 300px;
              margin-left: auto;
              margin-top: 15px;
              font-size: 15px;
            }
            .remarks-section {
              margin-top: 15px;
              font-size: 14px;
            }
            .signature-area {
              margin-top: 40px;
              display: flex;
              justify-content: space-between;
              gap: 20px;
              page-break-inside: avoid;
            }
            .watermark {
              position: absolute;
              top: 35%;
              left: 15%;
              font-size: 70px;
              font-weight: bold;
              color: rgba(0, 0, 0, 0.05);
              transform: rotate(-30deg);
              z-index: 0;
              pointer-events: none;
              white-space: nowrap;
            }
            .signature-box {
              width: 220px;
              text-align: center;
              border-top: 1px solid #000;
              padding-top: 5px;
            }
            .text-center {
              text-align: center;
            }
            @media print {
              .no-print {
                display: none;
              }
              body {
                margin: 0;
              }
              .watermark {
                position: absolute;
                top: 35%;
                left: 15%;
                font-size: 70px;
                font-weight: bold;
                color: rgba(0, 0, 0, 0.05);
                transform: rotate(-30deg);
                z-index: 0;
                pointer-events: none;
                white-space: nowrap;
              }
              .print-container {
                width: 100%;
                padding: 25px;
                position: relative;
              }
              .print-container > * {
                position: relative;
                z-index: 1;
              }
              table th, table td {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              thead { display: table-header-group; }
              tfoot { display: table-footer-group; }
              tr { page-break-inside: avoid; }
              .signature-area {
                page-break-inside: avoid;
                page-break-after: avoid;
                margin-top: 30px !important;
              }
              .summary-table {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.moveTo(0, 0);
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      }, 1000);
    };

    onMounted(() => {
      if (route.params.id) {
        loadPiData(route.params.id);
      } else {
        toast.error("No PI ID provided");
        router.push({ path: "/lc/import-pi-record" });
      }
    });

    return {
      loading,
      piData,
      cart,
      formatDate,
      formatNumber,
      goBack,
      downloadPDF,
      printPage,
      getCurrentCompany: computed(() => store.getters.getCurrentCompany),
      getFileServerUrl: computed(() => store.getters.getFileServerUrl),
    };
  },
};
</script>

<style scoped>
.print-container {
  width: 800px;
  margin: auto;
  padding: 25px;
  background: white;
  position: relative;
  min-height: 600px;
}

.watermark {
  position: absolute;
  top: 35%;
  left: 15%;
  font-size: 70px;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.05);
  transform: rotate(-30deg);
  z-index: 0;
  pointer-events: none;
  white-space: nowrap;
}

.header {
  text-align: center;
  border-bottom: 2px solid #333;
  padding-bottom: 10px;
  margin-bottom: 15px;
  position: relative;
  z-index: 1;
}

.header img {
  width: 120px;
  margin-bottom: 10px;
}

.logo-placeholder {
  margin-bottom: 10px;
}

.company-info {
  font-size: 14px;
  margin-top: 5px;
}

.pi-title {
  text-align: center;
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.pi-info {
  margin-bottom: 15px;
  font-size: 14px;
  color: #333;
}

.section-title {
  background: #f2f2f2;
  padding: 8px;
  margin-top: 20px;
  font-weight: bold;
  border-left: 4px solid #555;
  color: #333;
  position: relative;
  z-index: 1;
}

.flex-row {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
}

.flex-col {
  width: 48%;
  line-height: 22px;
  font-size: 14px;
  color: #555;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 14px;
}

.items-table th,
.items-table td {
  border: 1px solid #999;
  padding: 8px;
  text-align: left;
}

.items-table th {
  background: #eee;
  font-weight: bold;
  color: #333;
}

.items-table td {
  color: #555;
}

.items-table td:nth-child(3),
.items-table td:nth-child(4),
.items-table td:nth-child(5) {
  text-align: right;
}

.summary-table {
  width: 300px;
  margin-left: auto;
  margin-top: 10px;
  font-size: 15px;
  border-collapse: collapse;
}

.summary-table td {
  border: 1px solid #999;
  padding: 8px;
}

.summary-table tr:last-child td {
  font-weight: bold;
}

.remarks-section {
  margin-top: 15px;
  font-size: 14px;
  color: #555;
}

.signature-area {
  margin-top: 50px;
  display: flex;
  justify-content: space-between;
  gap: 20px;
  page-break-inside: avoid;
}

.signature-box {
  width: 220px;
  text-align: center;
  border-top: 1px solid #000;
  padding-top: 5px;
  font-size: 14px;
  color: #333;
}

.text-center {
  text-align: center;
}

.full-height {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

@media print {
  .d-print-none {
    display: none !important;
  }
  .print-container {
    width: 100%;
    padding: 20px;
    box-shadow: none;
  }
  .watermark {
    display: none;
  }
  .signature-area {
    page-break-inside: avoid;
    page-break-after: avoid;
    margin-top: 30px !important;
  }
  .summary-table {
    page-break-inside: avoid;
  }
  .section-title {
    page-break-after: avoid;
  }
  .items-table th,
  .items-table td,
  .summary-table td {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
